import { useEffect, useState } from "react";
import { api, formatDate, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import ConfirmDelete from "@/components/ConfirmDelete";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, ShieldCheck, KeyRound, UserRound, Wrench, Megaphone, HandCoins, Users as UsersIcon, PauseCircle, PlayCircle, Ban } from "lucide-react";
import { toast } from "sonner";
import { Navigate } from "react-router-dom";

const ROLE_LABELS = {
  admin: "Admin",
  co_admin: "Co-Admin",
  accountant: "Accountant",
  sales_manager: "Sales Manager",
  sales_rep: "Sales Representative",
  marketing_head: "Marketing Head",
  marketing_rep: "Marketing Representative",
  service_manager: "Service Manager",
  spare_parts_manager: "Spare Parts Manager",
  technician: "Technician",
};

const GROUP_META = {
  sales: { title: "Sales Team", icon: HandCoins, color: "#166534", subtitle: "Handles leads, quotes and closing sales." },
  marketing: { title: "Marketing Team", icon: Megaphone, color: "#D97706", subtitle: "Runs outreach, campaigns and lead nurture." },
  service: { title: "Service", icon: Wrench, color: "#0f766e", subtitle: "Service Manager, Spare Parts Manager and Technicians." },
  core: { title: "Core Management", icon: ShieldCheck, color: "#1f2937", subtitle: "Admins and Accountant — full financial oversight." },
};

const empty = { name: "", email: "", password: "", role: "sales_rep", phone: "" };

export default function Users() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ roles: [], no_login_roles: [], groups: {} });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [resetting, setResetting] = useState(null);
  const [newPwd, setNewPwd] = useState("");

  const load = async () => {
    const [u, m] = await Promise.all([api.get("/users"), api.get("/meta/roles")]);
    setRows(u.data);
    setMeta(m.data);
  };
  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  const noLogin = (meta.no_login_roles || []).includes(form.role);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (noLogin) payload.password = "";
      await api.post("/users", payload);
      toast.success(`${ROLE_LABELS[form.role] || form.role} added`);
      setOpen(false); setForm(empty); load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  const del = async (u) => {
    if (u.id === user.id) return toast.error("Cannot delete yourself");
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`Removed ${u.name || u.email}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to delete");
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (newPwd.length < 6) return toast.error("Password must be at least 6 characters");
    try {
      await api.post(`/users/${resetting.id}/reset-password`, { password: newPwd });
      toast.success(`Password reset for ${resetting.email}`);
      setResetting(null); setNewPwd("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  const togglePause = async (u) => {
    if (u.id === user.id) return toast.error("Cannot pause yourself");
    const paused = !!u.is_paused;
    try {
      await api.post(`/users/${u.id}/${paused ? "unpause" : "pause"}`);
      toast.success(paused
        ? `Access resumed for ${u.name || u.email}`
        : `Access paused for ${u.name || u.email}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  const groups = meta.groups || {};
  const usersByRole = (roleList) => rows.filter((u) => roleList.includes(u.role));

  return (
    <div className="p-8 lg:p-10 max-w-[1400px]">
      <PageHeader
        title="Team & Access"
        subtitle="Organize the Three Zee Agro Tech team. Technicians receive job updates via SMS/WhatsApp and do not sign in."
        testid="page-users-header"
        action={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setForm(empty); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-user-btn" className="bg-[#39FF14] hover:bg-[#2ECC71]">
                <Plus size={16} className="mr-2" /> Add Team Member
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading">New Team Member</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div><Label>Full name*</Label><Input required data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div><Label>Email*</Label><Input required type="email" data-testid="user-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label>Phone {noLogin && <span className="text-xs text-yellow-400">(required for SMS/WhatsApp)</span>}</Label>
                  <Input data-testid="user-phone-input" placeholder="+91…" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </div>
                <div>
                  <Label>Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v, password: (meta.no_login_roles || []).includes(v) ? "" : form.password })}>
                    <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(meta.roles || []).map((r) => (
                        <SelectItem key={r} value={r}>{ROLE_LABELS[r] || r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {!noLogin ? (
                  <div><Label>Password*</Label><Input required minLength={6} type="password" data-testid="user-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
                ) : (
                  <div className="text-xs bg-yellow-500/10 border border-yellow-500/30 text-yellow-200 rounded p-3">
                    Technicians <b>do not sign in</b>. They receive job assignments and updates via SMS/WhatsApp on the phone number above.
                  </div>
                )}
                <DialogFooter>
                  <Button type="submit" data-testid="user-submit-btn" className="bg-[#39FF14] hover:bg-[#2ECC71]">Create</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="grid grid-cols-1 gap-6">
        {Object.entries(groups).map(([groupKey, roles]) => {
          const gMeta = GROUP_META[groupKey] || { title: groupKey, icon: UsersIcon, color: "#166534", subtitle: "" };
          const Icon = gMeta.icon;
          const members = usersByRole(roles);
          return (
            <section key={groupKey} data-testid={`team-section-${groupKey}`} className="bg-[#121815] border border-[#3A4640] rounded-md shadow-sm overflow-hidden">
              <header className="px-5 py-4 border-b border-[#3A4640] flex items-center gap-3">
                <div className="w-9 h-9 rounded-md flex items-center justify-center text-white" style={{ background: gMeta.color }}>
                  <Icon size={16} />
                </div>
                <div className="flex-1">
                  <div className="font-heading font-bold text-[#F8F9FA]">{gMeta.title}</div>
                  <div className="text-xs text-[#8A8F98]">{gMeta.subtitle}</div>
                </div>
                <div className="text-xs font-heading uppercase tracking-wider text-[#8A8F98]">{members.length} member{members.length === 1 ? "" : "s"}</div>
              </header>
              {members.length === 0 ? (
                <div className="px-5 py-8 text-center text-[#8A8F98] text-sm">No {gMeta.title.toLowerCase()} yet.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-[#0B0F0C] border-b border-[#3A4640] text-xs uppercase tracking-wider text-[#8A8F98] font-heading">
                    <tr>
                      <th className="text-left px-4 py-3">Name</th>
                      <th className="text-left px-4 py-3">Role</th>
                      <th className="text-left px-4 py-3">Email</th>
                      <th className="text-left px-4 py-3">Phone</th>
                      <th className="text-left px-4 py-3">Access</th>
                      <th className="text-left px-4 py-3">Added</th>
                      <th className="text-right px-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody data-testid={`team-${groupKey}-tbody`}>
                    {members.map((u) => (
                      <tr key={u.id} className="border-b border-[#3A4640] last:border-0 hover:bg-[#0B0F0C]" data-testid={`team-row-${u.id}`}>
                        <td className="px-4 py-3 font-medium text-[#F8F9FA]">{u.name}</td>
                        <td className="px-4 py-3">
                          <span className="text-xs bg-[#121815] text-[#F8F9FA] px-2 py-1 rounded font-heading">{ROLE_LABELS[u.role] || u.role}</span>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                        <td className="px-4 py-3 font-mono text-xs">{u.phone || "-"}</td>
                        <td className="px-4 py-3 text-xs">
                          {(meta.no_login_roles || []).includes(u.role) || u.can_login === false ? (
                            <span className="inline-flex items-center gap-1 text-[#8A8F98]"><UserRound size={12} /> SMS / WhatsApp only</span>
                          ) : u.is_paused ? (
                            <span
                              className="inline-flex items-center gap-1 text-red-300"
                              title={
                                (u.paused_reason ? `${u.paused_reason}. ` : "Paused. ") +
                                (u.paused_at ? `On ${formatDate(u.paused_at)}` : "") +
                                (u.paused_by_name ? ` by ${u.paused_by_name}` : "")
                              }
                              data-testid={`user-status-paused-${u.id}`}
                            >
                              <Ban size={12} />
                              {u.paused_by === "system" ? "Auto-locked" : "Paused"}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[#39FF14]" data-testid={`user-status-active-${u.id}`}><ShieldCheck size={12} /> Can sign in</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-[#8A8F98]">{formatDate(u.created_at)}</td>
                        <td className="px-4 py-3 text-right">
                          {u.can_login !== false && !(meta.no_login_roles || []).includes(u.role) && u.id !== user.id && (
                            <button
                              onClick={() => togglePause(u)}
                              className={`p-1.5 rounded mr-1 ${u.is_paused ? "hover:bg-green-50 text-[#39FF14]" : "hover:bg-yellow-500/10 text-yellow-400"}`}
                              title={u.is_paused ? "Resume access" : "Pause access"}
                              data-testid={`${u.is_paused ? "resume" : "pause"}-user-${u.id}`}
                            >
                              {u.is_paused ? <PlayCircle size={14} /> : <PauseCircle size={14} />}
                            </button>
                          )}
                          {u.can_login !== false && !(meta.no_login_roles || []).includes(u.role) && (
                            <button onClick={() => setResetting(u)} className="p-1.5 hover:bg-yellow-500/10 text-yellow-400 rounded mr-1" title="Reset password" data-testid={`reset-password-${u.id}`}><KeyRound size={14} /></button>
                          )}
                          {u.id !== user.id && (
                            <ConfirmDelete
                              itemLabel={u.name || u.email}
                              entityLabel="team member"
                              onConfirm={() => del(u)}
                              testid={`user-${u.id}`}
                            >
                              <button className="p-1.5 hover:bg-red-500/10 text-red-600 rounded" data-testid={`delete-user-${u.id}`}><Trash2 size={14} /></button>
                            </ConfirmDelete>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          );
        })}
      </div>

      <Dialog open={!!resetting} onOpenChange={(v) => { if (!v) { setResetting(null); setNewPwd(""); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Reset Password</DialogTitle></DialogHeader>
          <form onSubmit={submitReset} className="space-y-4">
            <div className="text-sm text-[#8A8F98]">
              Set a new password for <span className="font-mono font-semibold text-[#F8F9FA]">{resetting?.email}</span>.
              Share the new password with them so they can log in.
            </div>
            <div>
              <Label>New password*</Label>
              <Input required type="text" data-testid="reset-password-input" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} placeholder="At least 6 characters" />
              <div className="text-xs text-[#8A8F98] mt-1">Shown in plain text so you can copy and share it.</div>
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="reset-password-submit" className="bg-[#39FF14] hover:bg-[#2ECC71]">Reset Password</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
